/**
 * Shared formatter for the "pending sync" tooltip rendered next to each
 * row that has an in-flight Cosmos pending change. All ProgramChanges
 * tabs (Benefits per tier, Market categories, Thresholds by market) use
 * the same wording so users see a consistent voice across pages.
 *
 * The structural minimum the formatter needs from a pending record is
 * `operation`, `createdAt`, `createdBy`, plus an optional `previousName`
 * for renames — every per-entity pending type in the app already
 * matches that shape.
 */

export interface PendingTooltipLike {
  operation: 'create' | 'update' | 'rename' | 'delete';
  createdAt: string;
  createdBy: string;
  /** Pre-rename name. Only relevant when `operation === 'rename'`. */
  previousName?: string;
}

export interface FormatPendingTooltipOptions {
  currentName?: string;

  updateSummary?: string;

  /**
   * Countries removed from this category that have not yet been assigned to
   * another category. When present, the tooltip appends a warning that these
   * removals will be ignored by the sync if they remain unassigned.
   */
  removedCountries?: string[];
}

const formatTimestamp = (iso: string): string => {
  const date = new Date(iso);
  return isNaN(date.getTime()) ? iso : date.toLocaleString('en-US');
};

export const formatPendingTooltip = (
  pending: PendingTooltipLike,
  options: FormatPendingTooltipOptions = {},
): string => {
  const when = formatTimestamp(pending.createdAt);
  const who = pending.createdBy || 'unknown user';
  const updateSummary = options.updateSummary ?? 'updated';

  let summary: string;
  switch (pending.operation) {
    case 'create':
      summary = `created by ${who}`;
      break;
    case 'delete':
      summary = `marked for deletion by ${who}`;
      break;
    case 'rename': {
      const prev = pending.previousName ?? '?';
      summary = options.currentName
        ? `renamed from "${prev}" to "${options.currentName}" by ${who}`
        : `renamed from "${prev}" by ${who}`;
      break;
    }
    case 'update':
    default:
      summary = `${updateSummary} by ${who}`;
      break;
  }

  return `Pending sync — ${summary} at ${when}. Changes are applied asynchronously; the committed state will update once the sync completes.`
    + formatRemovedCountriesWarning(options.removedCountries);
};

function formatRemovedCountriesWarning(countries?: string[]): string {
  if (!countries || countries.length === 0) return '';
  const list = countries.join(', ');
  return ` Note: ${list} ${countries.length === 1 ? 'has' : 'have'} been removed but not reassigned to another category. If not reassigned before the sync runs, the removal will be ignored.`;
}
